import { createStyles, withStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles } from '@paintscout/ui'
import { withDialogStackContext, withUser } from '@paintscout/ui-business'
import type { WithDialogStackContext, WithUser } from '@paintscout/ui-business'
import AddIcon from '@material-ui/icons/Add'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import CopyIcon from '@material-ui/icons/FileCopy'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import type { QuoteItemSection, RenderableItemSubstrate, UpdateableArea } from '@paintscout/util/builder'
import { getFeatures, getFiles, getFilesForItem, getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import classnames from 'classnames'
import type { OptionsDocument, QuoteDocument, QuoteFile, QuoteSource } from 'paintscout'
import React from 'react'
import FilesGrid from '../../../FilesGrid'
import EditItemFilesDialog from '../../../dialogs/EditItemFilesDialog'
import AreaSubstrates from '../AreaSubstrates'
import type { EnhancedQuoteFile } from '../../EditItemFilesDialog/FileView'

const MAKE_OPTION_KEY = 'make-option'
const MAKE_OPTION_LABEL = 'Make Option'

export interface AreaMainViewProps extends WithStyles<typeof styles>, WithDialogStackContext, WithUser {
  quote?: QuoteDocument
  options?: OptionsDocument
  area?: UpdateableArea
  onAddClick?: (event: React.MouseEvent<HTMLElement>) => void
  onItemClick?: (event: React.MouseEvent<HTMLElement>, item: RenderableItemSubstrate) => void
  onTotalsClick?: (event: React.MouseEvent<HTMLElement>) => void
  onReorder?: (args: { items: any[]; section: QuoteItemSection }) => void
  onItemActionClick?: (
    event: React.MouseEvent<HTMLElement>,
    actionName: string,
    item: RenderableItemSubstrate,
    section: QuoteItemSection
  ) => void
  onFilesChange?: (files: QuoteFile[], source?: QuoteSource, append?: boolean) => any
}

export interface AreaMainViewState {}

export const styles = (theme: Theme) => {
  return createStyles({
    root: {
      paddingBottom: theme.spacing(4)
    },
    bidSection: {
      // paddingTop: theme.spacing()
      // paddingBottom: theme.spacing()
    },
    optionsSection: {
      // paddingTop: theme.spacing(2),
      // paddingBottom: theme.spacing(2),
      '& thead': {
        background: theme.paintscout.palette.brand.Neutral.P94
      },
      '& th': {
        color: theme.palette.common.black
      }
    },
    archivedSection: {
      // paddingTop: theme.spacing(2),
      // paddingBottom: theme.spacing(2),
      '& thead': {
        background: theme.paintscout.palette.brand.Neutral.P94
      },
      '& th': {
        color: theme.palette.common.black
      }
    },
    toggleButtons: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center'
    },
    tableSubheading: {},
    formSection: {
      padding: 0
    },
    firstSection: {
      paddingTop: theme.spacing(2)
    }
  })
}

class AreaMainView extends React.Component<AreaMainViewProps, AreaMainViewState> {
  public render() {
    const { classes, quote, options, area, onFilesChange, user } = this.props
    const { isOfficeUser, isCrew, isAdmin, isCore } = user
    const objectLabels = getObjectLabels({ options })

    const files = getFilesForItem({ quote: quote, options, itemKey: this.props.area.key })
    const showFiles = files.length > 0

    const features = getFeatures({ options })
    const { allowQuoteAdditionalWork } = getQuoteOptions({ options })
    const hasAdditionalWork = features.quotes?.additionalWork && (quote.is_invoice || allowQuoteAdditionalWork)
    const hasOptionsApproval = options?.options?.optionsApproval?.enabled ?? false

    return (
      <div className={classes.root}>
        <div className={classnames(classes.formSection, classes.firstSection)}>
          <AreaSubstrates
            classes={{ root: classes.bidSection }}
            quote={quote}
            area={area}
            section={'bid'}
            options={options}
            label={objectLabels.quote.value}
            sublabel={`These items are used to generate the Area Description, and will appear on the ${objectLabels.workOrder.value}.`}
            showAddButton={!isOfficeUser && !isCrew}
            showTotals={true}
            onItemClick={this.props.onItemClick}
            onTotalsClick={this.props.onTotalsClick}
            onItemActionClick={this.props.onItemActionClick}
            onReorder={this.props.onReorder}
            onAddClick={this.props.onAddClick}
            tourIds={true}
            actions={
              !isOfficeUser && !isCrew
                ? [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: EditIcon
                    },
                    {
                      key: 'add-more',
                      label: 'Add More',
                      icon: AddIcon
                    },
                    {
                      key: MAKE_OPTION_KEY,
                      label: MAKE_OPTION_LABEL,
                      icon: AddBoxIcon
                    },
                    {
                      key: 'archive',
                      label: 'Archive',
                      icon: ArchiveIcon
                    },
                    {
                      key: 'copy',
                      label: 'Copy',
                      icon: CopyIcon
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: DeleteIcon
                    }
                  ]
                : isOfficeUser
                  ? [
                      {
                        key: 'edit',
                        label: 'Edit',
                        icon: EditIcon
                      }
                    ]
                  : []
            }
          />
        </div>

        <div className={classes.formSection}>
          <AreaSubstrates
            quote={quote}
            area={area}
            options={options}
            section={'options'}
            label={'Options'}
            sublabel={`These items will show below the main ${objectLabels.quote.value.toLowerCase()} items.`}
            hideEmpty={false}
            showAddButton
            onAddClick={this.props.onAddClick}
            onItemClick={this.props.onItemClick}
            onItemActionClick={this.props.onItemActionClick}
            onReorder={this.props.onReorder}
            classes={{ root: classes.optionsSection }}
            actions={[
              {
                key: 'edit',
                label: 'Edit',
                icon: EditIcon
              },
              {
                key: 'add-more',
                label: 'Add More',
                icon: AddIcon
              },
              hasAdditionalWork && { key: 'make-pending', label: `Move to Additional Work`, icon: AddBoxIcon },
              !isCrew &&
                !isOfficeUser && {
                  key: 'make-bid',
                  label: `Move to ${objectLabels.quote.value}`,
                  icon: AddBoxIcon
                },
              !quote.is_invoice &&
                hasOptionsApproval && {
                  key: 'move-to-added-options',
                  label: `Move to Added Options`,
                  icon: AddBoxIcon
                },
              {
                key: 'archive',
                label: 'Archive',
                icon: ArchiveIcon
              },
              {
                key: 'copy',
                label: 'Copy',
                icon: CopyIcon
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: DeleteIcon
              }
            ]}
          />
        </div>

        <div className={classes.formSection}>
          <AreaSubstrates
            quote={quote}
            area={area}
            options={options}
            section={'pending'}
            label={'Pending Approval'}
            sublabel={`These items will show below the main ${objectLabels.quote.value.toLowerCase()} items.`}
            hideEmpty={true}
            showAddButton={(isAdmin || isCore) && (!quote.is_invoice || !quote.parent_quote)}
            onAddClick={this.props.onAddClick}
            onItemClick={this.props.onItemClick}
            onItemActionClick={this.props.onItemActionClick}
            onReorder={this.props.onReorder}
            classes={{ root: classes.optionsSection }}
            actions={
              !isOfficeUser && !isCrew
                ? [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: EditIcon
                    },
                    {
                      key: 'add-more',
                      label: 'Add More',
                      icon: AddIcon
                    },
                    {
                      key: MAKE_OPTION_KEY,
                      label: MAKE_OPTION_LABEL,
                      icon: AddBoxIcon
                    },

                    {
                      key: 'archive',
                      label: 'Archive',
                      icon: ArchiveIcon
                    },
                    {
                      key: 'copy',
                      label: 'Copy',
                      icon: CopyIcon
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: DeleteIcon
                    }
                  ]
                : isOfficeUser
                  ? [
                      {
                        key: 'edit',
                        label: 'Edit',
                        icon: EditIcon
                      }
                    ]
                  : []
            }
          />
        </div>

        <div className={classes.formSection}>
          <AreaSubstrates
            quote={quote}
            area={area}
            options={options}
            section={'additional'}
            label={'Additional Work'}
            sublabel={`These items will show below the main ${objectLabels.quote.value.toLowerCase()} items.`}
            hideEmpty={true}
            showAddButton={false}
            onItemClick={this.props.onItemClick}
            onItemActionClick={this.props.onItemActionClick}
            onReorder={this.props.onReorder}
            classes={{ root: classes.optionsSection }}
            actions={
              !isOfficeUser && !isCrew
                ? [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: EditIcon
                    },
                    {
                      key: MAKE_OPTION_KEY,
                      label: MAKE_OPTION_LABEL,
                      icon: AddBoxIcon
                    },

                    {
                      key: 'archive',
                      label: 'Archive',
                      icon: ArchiveIcon
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: DeleteIcon
                    }
                  ]
                : isOfficeUser
                  ? [
                      {
                        key: 'edit',
                        label: 'Edit',
                        icon: EditIcon
                      }
                    ]
                  : []
            }
          />
        </div>

        <div className={classes.formSection}>
          <AreaSubstrates
            quote={quote}
            area={area}
            options={options}
            section={'added-options'}
            label={`${objectLabels.option.plural} ${
              quote.status.value === 'accepted' || quote.is_invoice ? 'Accepted' : 'Added'
            }`}
            sublabel={`These items will show below the main ${objectLabels.quote.value.toLowerCase()} items.`}
            hideEmpty={true}
            showAddButton={false}
            onAddClick={this.props.onAddClick}
            onItemClick={this.props.onItemClick}
            onItemActionClick={this.props.onItemActionClick}
            onReorder={this.props.onReorder}
            classes={{ root: classes.optionsSection }}
            actions={
              !isOfficeUser && !isCrew
                ? [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: EditIcon
                    },
                    {
                      key: MAKE_OPTION_KEY,
                      label: MAKE_OPTION_LABEL,
                      icon: AddBoxIcon
                    },
                    {
                      key: 'archive',
                      label: 'Archive',
                      icon: ArchiveIcon
                    },
                    {
                      key: 'copy',
                      label: 'Copy',
                      icon: CopyIcon
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: DeleteIcon
                    }
                  ]
                : isOfficeUser
                  ? [
                      {
                        key: 'edit',
                        label: 'Edit',
                        icon: EditIcon
                      }
                    ]
                  : []
            }
          />
        </div>

        <div className={classes.formSection}>
          <AreaSubstrates
            quote={quote}
            area={area}
            options={options}
            section={'archived'}
            label={'Archived'}
            hideEmpty={true}
            showAddButton={false}
            onItemClick={this.props.onItemClick}
            onItemActionClick={this.props.onItemActionClick}
            onReorder={this.props.onReorder}
            classes={{ root: classes.archivedSection }}
            actions={[
              {
                key: 'edit',
                label: 'Edit',
                icon: EditIcon
              },
              {
                key: 'add-more',
                label: 'Add More',
                icon: AddIcon
              },
              !isOfficeUser && !isCrew
                ? {
                    key: 'make-bid',
                    label: `Move to ${objectLabels.quote.value}`,
                    icon: ArchiveIcon
                  }
                : null,
              {
                key: MAKE_OPTION_KEY,
                label: MAKE_OPTION_LABEL,
                icon: AddBoxIcon
              },

              {
                key: 'delete',
                label: 'Delete',
                icon: DeleteIcon
              }
            ]}
          />
        </div>

        {showFiles && (
          <div className={classes.formSection}>
            <Typography variant="h4">Media</Typography>
            <FilesGrid
              files={files}
              editable={true}
              onReorder={(newFiles) => onFilesChange(newFiles, null, false)}
              showUploadDropzone={false}
              onFileClick={(index) => this.handleFileClick(files, index)}
              onUpload={(newFiles) => {
                if (newFiles && newFiles.length > 0) {
                  this.handleFileClick(files, files.length, newFiles)
                }
              }}
            />
          </div>
        )}
      </div>
    )
  }

  public handleFileClick = (files?: QuoteFile[], index?: number, uploadFiles?: File[]) => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    const numQuoteFiles = getFiles({ quote: this.props.quote, attached: true }).length
    const filesLeft = TRIAL_QUOTE_FILES_LIMIT - (numQuoteFiles + (uploadFiles?.length ?? 0))
    const isTrial = this.props.user?.isTrial

    openDialog(EditItemFilesDialog, {
      files,
      index,
      uploadFiles: uploadFiles && (isTrial ? uploadFiles.slice(0, filesLeft) : uploadFiles),
      onDelete: (_key: string, currentFiles: EnhancedQuoteFile[]) => {
        if (this.props.onFilesChange) {
          this.props.onFilesChange(currentFiles, null, false)
        }
      },
      onConfirm: (newFiles) => {
        if (this.props.onFilesChange) {
          this.props.onFilesChange(newFiles)
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }
}

export default withStyles(styles)(withDialogStackContext(withUser(AreaMainView)))
